/*!
 * Audio/Video tracking for Snowplow v3.23.1 (http://bit.ly/sp-js)
 * Copyright 2022 Snowplow Analytics Ltd
 * Licensed under BSD-3-Clause
 */

import { __spreadArray, __assign } from 'tslib';
import { dispatchToTrackersInCollection } from '@snowplow/browser-tracker-core';
import { buildSelfDescribingEvent } from '@snowplow/tracker-core';

// https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement#events
var MediaEvent = {
    ABORT: 'abort',
    CANPLAY: 'canplay',
    CANPLAYTHROUGH: 'canplaythrough',
    DURATIONCHANGE: 'durationchange',
    EMPTIED: 'emptied',
    ENDED: 'ended',
    ERROR: 'error',
    LOADEDMETADATA: 'loadedmetadata',
    LOADSTART: 'loadstart',
    PAUSE: 'pause',
    PLAY: 'play',
    PLAYING: 'playing',
    PROGRESS: 'progress',
    RATECHANGE: 'ratechange',
    RESIZE: 'resize',
    SEEKED: 'seeked',
    SEEKING: 'seeking',
    STALLED: 'stalled',
    SUSPEND: 'suspend',
    TIMEUPDATE: 'timeupdate',
    VOLUMECHANGE: 'volumechange',
    WAITING: 'waiting'
};
// https://developer.mozilla.org/en-US/docs/Web/API/HTMLVideoElement#events
var VideoEvent = {
    ENTERPICTUREINPICTURE: 'enterpictureinpicture',
    LEAVEPICTUREINPICTURE: 'leavepictureinpicture'
};
// https://developer.mozilla.org/en-US/docs/Web/API/TextTrack#events
var TextTrackEvent = {
    CHANGE: 'change'
};
// https://developer.mozilla.org/en-US/docs/Web/API/Document/fullscreenchange_event
var DocumentEvent = {
    FULLSCREENCHANGE: 'fullscreenchange'
};
var SnowplowEvent = {
    PERCENTPROGRESS: 'percentprogress',
    READY: 'ready'
};

var _a;
var READY_STATE = {
    0: 'HAVE_NOTHING',
    1: 'HAVE_METADATA',
    2: 'HAVE_CURRENT_DATA',
    3: 'HAVE_FUTURE_DATA',
    4: 'HAVE_ENOUGH_DATA'
};
var NETWORK_STATE = {
    0: 'NETWORK_EMPTY',
    1: 'NETWORK_IDLE',
    2: 'NETWORK_LOADING',
    3: 'NETWORK_NO_SOURCE'
};
var TAG = {
    VIDEO: 'VIDEO',
    AUDIO: 'AUDIO'
};
var SEARCH_ERROR = {
    NOT_FOUND: 'Media element not found',
    MULTIPLE_ELEMENTS: 'More than one media element in the provided node',
    PLYR_CURRENTSRC: 'Plyr currentSrc not updated'
};
var eventNames = (_a = {},
    _a[TextTrackEvent.CHANGE] = 'texttrackchange',
    _a);
Object.keys(eventNames).forEach(function (k) { return (eventNames[eventNames[k]] = k); });

var enumValues = function (enumType) { return Object.keys(enumType).map(function (key) { return enumType[key]; }); };
var AllEvents = __spreadArray(__spreadArray(__spreadArray(__spreadArray(__spreadArray([], enumValues(MediaEvent), true), enumValues(SnowplowEvent), true), enumValues(VideoEvent), true), enumValues(TextTrackEvent), true), enumValues(DocumentEvent), true);
var DefaultEvents = [
    SnowplowEvent.READY,
    MediaEvent.PAUSE,
    MediaEvent.PLAY,
    MediaEvent.SEEKED,
    MediaEvent.RATECHANGE,
    MediaEvent.VOLUMECHANGE,
    MediaEvent.ENDED,
    DocumentEvent.FULLSCREENCHANGE,
    SnowplowEvent.PERCENTPROGRESS,
];
var EventGroups = {
    AllEvents: AllEvents,
    DefaultEvents: DefaultEvents
};

function timeRangesToObjectArray(t) {
    var out = [];
    for (var i = 0; i < t.length; i++) {
        out.push({ start: t.start(i), end: t.end(i) });
    }
    return out;
}
function textTrackListToJson(textTrackList) {
    return Object.keys(textTrackList).map(function (_, i) {
        return {
            label: textTrackList[i].label,
            language: textTrackList[i].language,
            kind: textTrackList[i].kind,
            mode: textTrackList[i].mode
        };
    });
}
function isType(e, _type) {
    return (Object.keys(_type)
        .map(function (k) { return _type[k]; })
        .indexOf(e) !== -1);
}
function isElementFullScreen(id) {
    var _a;
    return ((_a = document.fullscreenElement) === null || _a === void 0 ? void 0 : _a.id) === id || false;
}
function boundaryErrorHandling(boundaries) {
    // Remove any elements that are out of bounds
    if (boundaries.some(function (b) { return b < 1 || 100 < b; })) {
        boundaries = boundaries.filter(function (b) { return 0 < b && b < 100; });
    }
    // Remove any duplicate elements
    if (boundaries.some(function (b, _, self) { return self.filter(function (f) { return f == b; }).length > 1; })) {
        boundaries = boundaries.filter(function (item, pos, self) { return self.indexOf(item) == pos; });
    }
    return boundaries;
}
function getUriFileExtension(uri) {
    // greedily match until the final '.' is found with trailing characters, capture those as extension
    // only capture until finding URI metacharacters
    var pattern = /.+\.([^\.?&#]+)/;
    var uriPath = uri;
    try {
        uriPath = new URL(uri).pathname;
    }
    catch (e) { }
    // try to match against the pathname only first, if unsuccessful try against the full URI
    var match = pattern.exec(uriPath) || pattern.exec(uri);
    return match && match[1];
}
function trackingOptionsParser(id, trackingOptions) {
    var defaults = {
        id: id,
        captureEvents: DefaultEvents,
        progress: {
            boundaries: [10, 25, 50, 75],
            boundaryTimeoutIds: []
        },
        volume: {
            trackingInterval: 250
        }
    };
    if (!trackingOptions)
        return defaults;
    if (trackingOptions === null || trackingOptions === void 0 ? void 0 : trackingOptions.captureEvents) {
        var parsedEvents_1 = [];
        trackingOptions.captureEvents.forEach(function (ev) {
            // If an event is an EventGroup, get the events from that group
            if (EventGroups.hasOwnProperty(ev)) {
                parsedEvents_1 = parsedEvents_1.concat(EventGroups[ev]);
            }
            else if (!Object.keys(AllEvents).filter(function (k) { return k === ev; })) {
                console.warn("'".concat(ev, "' is not a valid event."));
            }
            else {
                parsedEvents_1.push(ev in eventNames ? eventNames[ev] : ev);
            }
        });
        trackingOptions.captureEvents = parsedEvents_1;
        if (trackingOptions.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== -1) {
            defaults.progress = {
                boundaries: (trackingOptions === null || trackingOptions === void 0 ? void 0 : trackingOptions.boundaries) || defaults.progress.boundaries,
                boundaryTimeoutIds: []
            };
        }
        if (trackingOptions.captureEvents.indexOf(MediaEvent.VOLUMECHANGE) !== -1) {
            defaults.volume = {
                trackingInterval: (trackingOptions === null || trackingOptions === void 0 ? void 0 : trackingOptions.volumeChangeTrackingInterval) || defaults.volume.trackingInterval
            };
        }
    }
    return __assign(__assign({}, defaults), trackingOptions);
}
// Checks if a url is a data_url, so we don't send a (potentially large) payload
function dataUrlHandler(url) {
    if (url.indexOf('data:') !== -1) {
        return 'DATA_URL';
    }
    return url;
}

function findMediaElem(id) {
    var el = document.getElementById(id);
    if (!el)
        return { err: SEARCH_ERROR.NOT_FOUND };
    if (isAudioElement(el))
        return { el: el };
    if (isVideoElement(el)) {
        // Plyr loads in an initial blank video with currentSrc as https://cdn.plyr.io/static/blank.mp4
        // so we need to check until currentSrc updates
        if (el.currentSrc === 'https://cdn.plyr.io/static/blank.mp4' && el.readyState === 0) {
            return { err: SEARCH_ERROR.PLYR_CURRENTSRC };
        }
        return { el: el };
    }
    return findMediaElementChild(el);
}
function findMediaElementChild(el) {
    for (var _i = 0, _a = Object.keys(TAG); _i < _a.length; _i++) {
        var tag = _a[_i];
        var elem = el.getElementsByTagName(tag);
        if (elem.length === 1) {
            if (isAudioElement(elem[0]))
                return { el: elem[0] };
            if (isVideoElement(elem[0]))
                return { el: elem[0] };
        }
        else if (elem.length === 2 && tag === 'VIDEO' && isVideoElement(elem[0])) {
            // Special JWPlayer case where two video elements are used for cover-video effect.
            // In that case, we select the first video element.
            return { el: elem[0] };
        }
        else if (elem.length > 1) {
            return { err: SEARCH_ERROR.MULTIPLE_ELEMENTS };
        }
    }
    return { err: SEARCH_ERROR.NOT_FOUND };
}
function isAudioElement(el) {
    return el.tagName === TAG.AUDIO;
}
function isVideoElement(el) {
    return el.tagName === TAG.VIDEO;
}

function buildMediaEvent(e, el, conf, detail) {
    var context = [getHTMLMediaElementEntities(el, conf), getMediaPlayerEntities(el, detail)];
    if (el instanceof HTMLVideoElement)
        context.push(getHTMLVideoElementEntities(el));
    var data = { type: e in eventNames ? eventNames[e] : e };
    if (conf.label)
        data.label = conf.label;
    return {
        schema: 'iglu:com.snowplowanalytics.snowplow/media_player_event/jsonschema/1-0-0',
        data: data,
        context: context
    };
}
function getMediaPlayerEntities(el, detail) {
    var data = {
        currentTime: el.currentTime || 0,
        duration: el.duration || 0,
        ended: el.ended,
        loop: el.loop,
        muted: el.muted,
        paused: el.paused,
        playbackRate: el.playbackRate,
        volume: parseInt(String(el.volume * 100))
    };
    if (detail === null || detail === void 0 ? void 0 : detail.boundary) {
        data.percentProgress = detail.boundary;
    }
    return {
        schema: 'iglu:com.snowplowanalytics.snowplow/media_player/jsonschema/1-0-0',
        data: data
    };
}
function getHTMLMediaElementEntities(el, conf) {
    var _a, _b, _c;
    // In cases where the media does not have explicit id, we use the container id passed from the configuration.
    var htmlId = el.id || conf.id;
    var data = {
        htmlId: htmlId,
        mediaType: el.tagName,
        autoPlay: el.autoplay,
        buffered: timeRangesToObjectArray(el.buffered),
        controls: el.controls,
        currentSrc: el.currentSrc,
        defaultMuted: el.defaultMuted || false,
        defaultPlaybackRate: el.defaultPlaybackRate,
        error: el.error ? { code: (_a = el.error) === null || _a === void 0 ? void 0 : _a.code, message: (_b = el.error) === null || _b === void 0 ? void 0 : _b.message } : null,
        networkState: NETWORK_STATE[el.networkState],
        preload: el.preload,
        readyState: READY_STATE[el.readyState],
        seekable: timeRangesToObjectArray(el.seekable),
        seeking: el.seeking,
        src: dataUrlHandler(el.src || el.currentSrc),
        textTracks: textTrackListToJson(el.textTracks),
        fileExtension: getUriFileExtension(el.currentSrc),
        fullscreen: isElementFullScreen(el.id),
        pictureInPicture: ((_c = document.pictureInPictureElement) === null || _c === void 0 ? void 0 : _c.id) === el.id
    };
    if (el.disableRemotePlayback)
        data.disableRemotePlayback = el.disableRemotePlayback;
    if (el.crossOrigin)
        data.crossOrigin = el.crossOrigin;
    return {
        schema: 'iglu:org.whatwg/media_element/jsonschema/1-0-0',
        data: data
    };
}
function getHTMLVideoElementEntities(el) {
    var data = {
        poster: el.poster,
        videoHeight: el.videoHeight,
        videoWidth: el.videoWidth
    };
    if (el.autoPictureInPicture)
        data.autoPictureInPicture = el.autoPictureInPicture;
    if (el.disablePictureInPicture)
        data.disablePictureInPicture = el.disablePictureInPicture;
    return {
        schema: 'iglu:org.whatwg/video_element/jsonschema/1-0-0',
        data: data
    };
}

/*
 * Copyright (c) 2022 Snowplow Analytics Ltd
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 *    list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 *    this list of conditions and the following disclaimer in the documentation
 *    and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 *    contributors may be used to endorse or promote products derived from
 *    this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE
 * FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL
 * DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY,
 * OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE
 * OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
var LOG;
var _trackers = {};
function MediaTrackingPlugin() {
    return {
        activateBrowserPlugin: function (tracker) {
            _trackers[tracker.id] = tracker;
        },
        logger: function (logger) {
            LOG = logger;
        }
    };
}
var trackedIds = {};
// IE doesn't assign the 'target' property to the element a fullscreen event was called from,
// so need to track the element that is fullscreen, so when leaving fullscreen, we can
// check which id to track the event for (as document.fullscreenElement is null at that point)
var fullscreenElementId = '';
function enableMediaTracking(args) {
    var _a;
    var conf = trackingOptionsParser(args.id, args.options);
    // Some events have additional effects/need to perform checks before running mediaPlayerEvent
    var eventsWithOtherFunctions = (_a = {},
        _a[DocumentEvent.FULLSCREENCHANGE] = function (e, el, conf) {
            var _a, _b;
            // If there is no fullscreen element, it means we are entering fullscreen
            if (document.fullscreenElement || document.msFullscreenElement) {
                fullscreenElementId = ((_a = document.fullscreenElement) === null || _a === void 0 ? void 0 : _a.id) || ((_b = document.msFullscreenElement) === null || _b === void 0 ? void 0 : _b.id);
            }
            if (fullscreenElementId === el.id) {
                mediaPlayerEvent(e, el, conf);
            }
        },
        _a[MediaEvent.SEEKED] = function (eventName, el, conf) {
            if (conf.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== 0) {
                while (conf.progress.boundaryTimeoutIds.length) {
                    clearTimeout(conf.progress.boundaryTimeoutIds.pop());
                }
                mediaPlayerEvent(eventName, el, conf);
                setPercentageBoundTimeouts(el, conf);
            }
        },
        _a);
    var eventHandlers = {};
    conf.captureEvents.forEach(function (ev) {
        if (eventsWithOtherFunctions.hasOwnProperty(ev)) {
            eventHandlers[ev] = function (e, el, conf, event) {
                return eventsWithOtherFunctions[ev](e, el, conf, event);
            };
        }
        else {
            eventHandlers[ev] = function (e, el, conf) {
                return mediaPlayerEvent(e, el, conf);
            };
        }
    });
    trackedIds[args.id] = { waitTime: 250, retryCount: 5, tracking: false };
    setUpListeners(args.id, conf, eventHandlers);
}
function setUpListeners(id, conf, eventHandlers) {
    // The element may not be loaded in time for this function to run, so we have a few goes at finding the element
    var result = findMediaElem(id);
    if (!trackedIds[id].retryCount) {
        LOG.error(result.err || SEARCH_ERROR.NOT_FOUND);
        return;
    }
    if (!result.el) {
        trackedIds[id].retryCount--;
        trackedIds[id].timeoutId = setTimeout(function () { return setUpListeners(id, conf, eventHandlers); }, trackedIds[id].waitTime);
        trackedIds[id].waitTime *= 2;
        return;
    }
    clearTimeout(trackedIds[id].timeoutId);
    if (!trackedIds[id].tracking) {
        if (conf.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== 0) {
            boundaryErrorHandling(conf.progress.boundaries);
            setPercentageBoundTimeouts(result.el, conf);
        }
        addCaptureEventListeners(result.el, conf, eventHandlers);
        trackedIds[id].tracking = true;
    }
}
function addCaptureEventListeners(el, conf, eventHandlers) {
    if (conf.captureEvents.indexOf(SnowplowEvent.READY) !== -1) {
        mediaPlayerEvent(SnowplowEvent.READY, el, conf);
    }
    conf.captureEvents.forEach(function (e) {
        if (isType(e, TextTrackEvent)) {
            // IE10 doesn't support addEventListener for TextTrack objects
            if (el.textTracks.hasOwnProperty('addEventListener')) {
                el.textTracks.addEventListener(e, function () { return eventHandlers[e](e, el, conf); });
            }
        }
        else if (isType(e, DocumentEvent)) {
            // The event emitted by the listener is needed for fullscreen tracking, as on exiting fullscreen
            // document.fullscreenElement is null. The id is needed to check if the event should be tracked
            ['MSFullscreenChange', 'fullscreenchange'].forEach(function (p) {
                document.addEventListener(p, function () { return eventHandlers[e](e, el, conf); });
            });
        }
        else {
            el.addEventListener(e, function () { return eventHandlers[e](e, el, conf); });
        }
    });
}
function mediaPlayerEvent(e, el, conf, detail) {
    var event = buildMediaEvent(e, el, conf, detail);
    if (conf.captureEvents.indexOf(SnowplowEvent.PERCENTPROGRESS) !== -1) {
        progressHandler(e, el, conf);
    }
    // Dragging the volume scrubber will generate a lot of events, this limits the rate at which
    // volume events can be sent at
    if (e === MediaEvent.VOLUMECHANGE) {
        clearTimeout(conf.volume.eventTimeoutId);
        conf.volume.eventTimeoutId = setTimeout(function () { return trackMediaEvent(event); }, conf.volume.trackingInterval);
    }
    else {
        trackMediaEvent(event);
    }
}
function trackMediaEvent(event, trackers) {
    if (trackers === void 0) { trackers = Object.keys(_trackers); }
    dispatchToTrackersInCollection(trackers, _trackers, function (t) {
        t.core.track(buildSelfDescribingEvent({ event: event }), event.context, event.timestamp);
    });
}
// Progress Tracking
function progressHandler(e, el, conf) {
    // If the event is a pause event, we need to clear any existing progress timeouts, and set them again on play
    if (e === MediaEvent.PAUSE) {
        while (conf.progress.boundaryTimeoutIds.length) {
            clearTimeout(conf.progress.boundaryTimeoutIds.pop());
        }
    }
    if (e === MediaEvent.PLAY && el.readyState > 0) {
        setPercentageBoundTimeouts(el, conf);
    }
}
function setPercentageBoundTimeouts(el, conf) {
    conf.progress.boundaries.forEach(function (boundary) {
        var absoluteBoundaryTimeMs = el.duration * (boundary / 100) * 1000;
        var timeUntilBoundaryEvent = absoluteBoundaryTimeMs - el.currentTime * 1000;
        // If the boundary is less than the current time, we don't need to bother setting it
        if (0 < timeUntilBoundaryEvent) {
            conf.progress.boundaryTimeoutIds.push(setTimeout(function () { return waitAnyRemainingTimeAfterTimeout(el, absoluteBoundaryTimeMs, boundary, conf); }, timeUntilBoundaryEvent));
        }
    });
}
// The timeout in setPercentageBoundTimeouts fires ~100 - 300ms early
// waitAnyRemainingTimeAfterTimeout ensures the event is fired accurately
function waitAnyRemainingTimeAfterTimeout(el, absoluteBoundaryTimeMs, boundary, conf) {
    if (el.currentTime * 1000 < absoluteBoundaryTimeMs) {
        setTimeout(function () {
            waitAnyRemainingTimeAfterTimeout(el, absoluteBoundaryTimeMs, boundary, conf);
        }, 10);
    }
    else {
        mediaPlayerEvent(SnowplowEvent.PERCENTPROGRESS, el, conf, { boundary: boundary });
    }
}

export { MediaTrackingPlugin, enableMediaTracking };
//# sourceMappingURL=index.module.js.map
